import { NextRouter } from "next/router"
import {
  FlightExpirationException,
  FlightSeatMapUnavailableException,
} from "@/types/flights/exceptions"
import { FlightBookingMode } from "@/types/flights/flight-booking"
import { FlightBookingApi } from "@/types/flights/flight-booking-api"
import { FlightSeatMapValue } from "@/types/flights/flight-seat-map"
import { CurrentUser } from "@/types/user"
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { appLandingPath } from "@/lib/auth-utils"
import { buildClientAxios } from "@/lib/client-axios"
import { FlightApi } from "@/lib/flight-api"
import {
  redirectToDepartureSearch,
  reviseFlightItinerary,
} from "@/lib/flight-booking-utils"
import {
  calcBookingFormValid,
  emptyBookingFormState,
  FlightBookingFormOptions,
  FlightBookingFormState,
  FlightTravelerFormValue,
  validateTravelerFormValue,
} from "@/lib/flight-form-utils"
import {
  buildFlightSeatMapValue,
  flightBookingTravelers,
} from "@/lib/flight-utils"
import {
  CreditCardFormValue,
  validateCreditCardFormValue,
} from "@/lib/form-utils"
import {
  BillingFormValue,
  validateBillingFormFormValue,
} from "@/lib/phone-utils"
import { AppDispatch, type RootState } from "@/lib/storage/redux-store"
import { assertExists } from "@/lib/utils/assertions"

export const summary_step = 1
export const review_and_pay_step = 2
export const submission_step = 3
export const confirmation_step = 4

export type FlightBookingStep =
  | typeof summary_step
  | typeof review_and_pay_step
  | typeof submission_step
  | typeof confirmation_step

export interface FlightBookingSliceState {
  error: string | undefined
  expired: boolean
  flightBooking: FlightBookingApi | undefined
  formOptions: FlightBookingFormOptions
  step: FlightBookingStep
  seatMapValue: FlightSeatMapValue | undefined
  seatMapLoading: boolean
  seatMapError: string | undefined
  bookingFormState: FlightBookingFormState
  showRecoverableErrorsDialog: boolean
  recoverableErrors?: string[]
  confirmation: {
    loading: boolean
    error: string | undefined
    showCancellationDialog: boolean
  }
  bookingMode: FlightBookingMode
  vendorCheckoutResetUrl: string | undefined
  vendorCheckoutResetLoading: boolean
}

export const flightBookingSliceInitialState: FlightBookingSliceState = {
  error: undefined,
  expired: false,
  flightBooking: undefined,
  formOptions: {
    locations: [],
    credit_card_types: [],
    meal_preferences: [],
  },
  step: summary_step,
  seatMapValue: undefined,
  seatMapLoading: false,
  seatMapError: undefined,
  showRecoverableErrorsDialog: false,
  bookingFormState: emptyBookingFormState(),
  confirmation: {
    loading: false,
    error: undefined,
    showCancellationDialog: false,
  },
  bookingMode: "sandbox",
  vendorCheckoutResetUrl: undefined,
  vendorCheckoutResetLoading: false,
}

// region Selectors

const selectFlightBookingSlice = (state: RootState) => state.flightBookingSlice

export const selectFlightBooking = createSelector(
  [selectFlightBookingSlice],
  (state) => state.flightBooking
)

export const selectFormOptions = createSelector(
  [selectFlightBookingSlice],
  (state) => state.formOptions
)

export const selectError = createSelector(
  [selectFlightBookingSlice],
  (state) => state.error
)

export const selectStep = createSelector(
  [selectFlightBookingSlice],
  (state) => state.step
)

export const selectSeatMapValue = createSelector(
  [selectFlightBookingSlice],
  (state) => state.seatMapValue
)

export const selectSeatMapLoading = createSelector(
  [selectFlightBookingSlice],
  (state) => state.seatMapLoading
)

export const selectSeatMapError = createSelector(
  [selectFlightBookingSlice],
  (state) => state.seatMapError
)

export const selectConfirmationState = createSelector(
  [selectFlightBookingSlice],
  (state) => state.confirmation
)

export const selectVendorCheckoutResetUrl = createSelector(
  [selectFlightBookingSlice],
  (state) => state.vendorCheckoutResetUrl
)

export const selectVendorCheckoutResetLoading = createSelector(
  [selectFlightBookingSlice],
  (state) => state.vendorCheckoutResetLoading
)

export const selectBookingFormState = createSelector(
  [selectFlightBookingSlice],
  (state) => state.bookingFormState
)

export const selecteBookingShowValidations = createSelector(
  [selectBookingFormState],
  (state) => state.showValidations
)

export const selectBookingSubmitting = createSelector(
  [selectBookingFormState],
  (state) => state.submitting
)

export const selectBookingSubmitError = createSelector(
  [selectBookingFormState],
  (state) => state.submitError
)

export const selectRecoverableErrorDetails = createSelector(
  [selectFlightBookingSlice],
  (state) => {
    return {
      showRecoverableErrorsDialog: state.showRecoverableErrorsDialog,
      recoverableErrors: state.recoverableErrors,
    }
  }
)

// endregion

// region helpers

function emptyFormOptions() {
  return {
    locations: [],
    credit_card_types: [],
    meal_preferences: [],
  }
}

function getStepFromStatus(flightBooking: FlightBookingApi | undefined) {
  switch (flightBooking?.status ?? "") {
    case "pending-confirm":
    case "ticketing-error":
    case "confirmed":
    case "partial-cancelled":
    case "cancelled":
    case "pending-cancel":
      return confirmation_step
    case "submitting":
      return submission_step
    default:
      return summary_step
  }
}

// endregion

// region Thunks

export const backToChatFromSummary = createAsyncThunk<
  void,
  { router: NextRouter },
  {
    dispatch: AppDispatch
    state: RootState
  }
>("flightBooking/backToChatFromSummary", async ({ router }, { getState }) => {
  const state = getState()
  const flightBooking = selectFlightBooking(state)
  const flightItinerary = flightBooking?.flight_itinerary
  if (!flightItinerary) {
    await router.push(appLandingPath())
  } else {
    reviseFlightItinerary(flightItinerary, router)
  }
})

export const initBookingConfirmation = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightBooking/initBookingConfirmation",
  async (_, { dispatch, getState }) => {
    const flightApi = new FlightApi(buildClientAxios())
    try {
      const flightBooking = selectFlightBooking(getState())
      if (flightBooking?.id) {
        dispatch(flightBookingSlice.actions.bookingConfirmationFetchPending())
        const res = await flightApi.refreshFlightBooking(flightBooking.id)
        dispatch(
          flightBookingSlice.actions.bookingConfirmationFetchFulfilled(res)
        )
      }
    } catch (e) {
      console.error(e)
      dispatch(
        flightBookingSlice.actions.bookingConfirmationFetchError(
          "Error retrieving flight booking information"
        )
      )
    }
  }
)

export const confirmAndPay = createAsyncThunk<
  void,
  { router: NextRouter },
  {
    dispatch: AppDispatch
    state: RootState
  }
>("flightBooking/confirmAnPay", async ({ router }, { dispatch, getState }) => {
  const flightApi = new FlightApi(buildClientAxios())

  const flightBooking = selectFlightBooking(getState())

  if (flightBooking?.id == null) return

  dispatch(flightBookingSlice.actions.setShowValidations(true))

  const bookingFormState = selectBookingFormState(getState())
  if (bookingFormState.valid) {
    dispatch(flightBookingSlice.actions.bookingSubmitPending())
    try {
      const captureEndpoint = await flightApi.buildBookingCaptureEndpoint(
        flightBooking.id
      )
      const credit_card = assertExists(bookingFormState.credit_card[1])
      const captureRes = await flightApi.captureBookingVaultData(
        captureEndpoint,
        credit_card
      )

      const res = await flightApi.submitFlightBooking(flightBooking.id, {
        travelers: bookingFormState.travelers.map((x) => assertExists(x[1])),
        billing: assertExists(bookingFormState.billing[1]),
        seat_map_details: selectSeatMapValue(getState())?.seatMapDetails ?? [],
        capture_details: {
          ...captureRes,
          unique_id: captureEndpoint.unique_id,
        },
      })
      dispatch(flightBookingSlice.actions.bookingSubmitFulfilled(res))
      // Jan 16, 2026 - clean up amplitude - no one is really looking at these revenues
      // sendAmplitudeFlightRevenue(res)
    } catch (e) {
      if (e instanceof FlightExpirationException) {
        sendAnalyticsEvent({
          name: "FlightBookingConfirmAndPayError",
          properties: {
            error: e.message,
          },
        })
        const flightItinerary =
          selectFlightBooking(getState())?.flight_itinerary
        if (flightItinerary) {
          redirectToDepartureSearch(flightItinerary, router, { expired: true })
        }
        return
      }
      dispatch(
        flightBookingSlice.actions.bookingSubmitError(
          "Could not submit booking at this time. Please try again later."
        )
      )
    }
  }
})

export const resetVendorCheckout = createAsyncThunk<
  void,
  { router: NextRouter },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightBooking/resetVendorCheckout",
  async ({ router }, { dispatch, getState }) => {
    const slice = getState().flightBookingSlice
    if (slice.vendorCheckoutResetLoading) return
    if (slice.vendorCheckoutResetUrl) return
    const flightBooking = slice.flightBooking
    if (!flightBooking?.id) return

    dispatch(flightBookingSlice.actions.vendorCheckoutResetPending())
    try {
      const flightApi = new FlightApi(buildClientAxios())
      const travelers = flightBookingTravelers(flightBooking)
      const res = await flightApi.vendorCheckout(flightBooking.id, travelers)
      dispatch(
        flightBookingSlice.actions.vendorCheckoutResetFulfilled(res.iframe_url)
      )
    } catch (e) {
      console.error(e)
      dispatch(flightBookingSlice.actions.vendorCheckoutResetError())
      const flightItinerary = selectFlightBooking(getState())?.flight_itinerary
      if (flightItinerary) {
        redirectToDepartureSearch(flightItinerary, router)
      }
    }
  }
)

export const fetchSeatMap = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("flightBooking/fetchSeatMap", async (_, { dispatch, getState }) => {
  const flightBooking = selectFlightBooking(getState())
  if (!flightBooking) return

  const flightApi = new FlightApi(buildClientAxios())
  dispatch(flightBookingSlice.actions.seatMapFetchPending())
  try {
    const res = await buildFlightSeatMapValue(flightBooking, flightApi)
    dispatch(flightBookingSlice.actions.seatMapFetchFulfilled(res))
  } catch (e) {
    if (e instanceof FlightSeatMapUnavailableException) {
      dispatch(
        flightBookingSlice.actions.seatMapFetchError(
          "Seat map unavailable for selected flight(s)."
        )
      )
    } else {
      console.error(e)
      dispatch(
        flightBookingSlice.actions.seatMapFetchError(
          "Error fetching seat map details"
        )
      )
    }
  }
})

// endregion

const flightBookingSlice = createSlice({
  name: "flightBooking",
  initialState: flightBookingSliceInitialState,
  reducers: {
    pageHydrate(
      state,
      action: PayloadAction<{
        flightBooking: FlightBookingApi | undefined
        error: string | undefined
        expired: boolean
        formOptions: FlightBookingFormOptions | undefined
        bookingMode: FlightBookingMode
        user: CurrentUser
      }>
    ) {
      state.bookingMode = action.payload.bookingMode
      state.error = action.payload.error
      state.expired = action.payload.expired
      state.flightBooking = action.payload.flightBooking
      state.step = getStepFromStatus(action.payload.flightBooking)
      state.formOptions = action.payload.formOptions ?? emptyFormOptions()
      state.bookingFormState = emptyBookingFormState(
        state.flightBooking,
        state.formOptions,
        action.payload.user
      )
      state.vendorCheckoutResetUrl = undefined
      state.vendorCheckoutResetLoading = false
    },
    goToReviewAndPay(state) {
      state.step = review_and_pay_step
    },
    goToSummary(state) {
      state.step = summary_step
    },
    reset: () => flightBookingSliceInitialState,
    setSeatMapValue(state, action: PayloadAction<FlightSeatMapValue>) {
      state.seatMapValue = action.payload
    },
    seatMapFetchPending(state) {
      state.seatMapLoading = true
      state.seatMapError = undefined
      state.seatMapValue = undefined
    },
    seatMapFetchFulfilled(state, action: PayloadAction<FlightSeatMapValue>) {
      state.seatMapLoading = false
      state.seatMapValue = action.payload
      state.seatMapError = undefined
    },
    seatMapFetchError(state, action: PayloadAction<string>) {
      state.seatMapLoading = false
      state.seatMapError = action.payload
      state.seatMapValue = undefined
    },
    seatMapUpdated(state, action: PayloadAction<FlightSeatMapValue>) {
      state.seatMapValue = action.payload
    },
    bookingConfirmationFetchPending(state) {
      state.confirmation.loading = true
      state.confirmation.error = undefined
    },
    bookingConfirmationFetchFulfilled(
      state,
      action: PayloadAction<FlightBookingApi>
    ) {
      state.flightBooking = action.payload
      state.confirmation.loading = false
      state.confirmation.error = undefined
    },
    bookingConfirmationFetchError(state, action: PayloadAction<string>) {
      state.confirmation.loading = false
      state.confirmation.error = action.payload
    },
    vendorCheckoutResetPending(state) {
      state.vendorCheckoutResetLoading = true
    },
    vendorCheckoutResetFulfilled(state, action: PayloadAction<string>) {
      state.vendorCheckoutResetLoading = false
      state.vendorCheckoutResetUrl = action.payload
    },
    vendorCheckoutResetError(state) {
      state.vendorCheckoutResetLoading = false
    },
    showCancellationDialog(state, action: PayloadAction<boolean>) {
      state.confirmation.showCancellationDialog = action.payload
    },
    updateTravelerForm(
      state,
      action: PayloadAction<{ ix: number; value: FlightTravelerFormValue }>
    ) {
      // setting these update fns as a callback so it doesn't trigger a re-render
      const [_, validated, errors] = validateTravelerFormValue(
        action.payload.value,
        state.formOptions
      )
      state.bookingFormState.travelers[action.payload.ix] = [
        action.payload.value,
        validated,
        errors,
      ]

      if (state.seatMapValue) {
        state.seatMapValue.travelers = state.bookingFormState.travelers.map(
          (t, tix) => {
            return {
              travelerIx: tix,
              traveler: t[0],
            }
          }
        )
      }

      state.bookingFormState.valid = calcBookingFormValid(
        state.bookingFormState
      )
    },
    updateCreditCardForm(state, action: PayloadAction<CreditCardFormValue>) {
      const [_, validated, errors] = validateCreditCardFormValue(
        action.payload,
        state.formOptions
      )
      state.bookingFormState.credit_card = [action.payload, validated, errors]

      state.bookingFormState.valid = calcBookingFormValid(
        state.bookingFormState
      )
    },
    updateBillingForm(state, action: PayloadAction<BillingFormValue>) {
      const [_, validated, errors] = validateBillingFormFormValue(
        action.payload,
        state.formOptions,
        "flight"
      )
      state.bookingFormState.billing = [action.payload, validated, errors]

      state.bookingFormState.valid = calcBookingFormValid(
        state.bookingFormState
      )
    },
    setShowValidations(state, action: PayloadAction<boolean>) {
      state.bookingFormState.showValidations = action.payload
    },
    bookingSubmitPending(state) {
      state.bookingFormState.submitting = true
      state.bookingFormState.submitError = undefined
    },
    bookingSubmitFulfilled(state, action: PayloadAction<FlightBookingApi>) {
      state.bookingFormState.submitting = false
      state.bookingFormState.submitError = undefined
      state.flightBooking = action.payload
      state.step = submission_step
    },
    bookingSubmitError(state, action: PayloadAction<string>) {
      state.bookingFormState.submitting = false
      state.bookingFormState.submitError = action.payload
    },
    showRecoverableErrorsDialog(state, action: PayloadAction<string[]>) {
      state.step = review_and_pay_step
      state.recoverableErrors = action.payload
      state.showRecoverableErrorsDialog = true
    },
    closeRecoverableErrorsDialog(state) {
      state.showRecoverableErrorsDialog = false
    },
  },
})

export type FlightBookingSlice = typeof flightBookingSlice

export default flightBookingSlice
